import React from 'react';
import styles from './CustomerCard.module.css';

/**
 * Formata um timestamp ISO para uma string de tempo relativo (ex: "há 5 min").
 * @param {string} timestamp - O timestamp no formato ISO.
 * @returns {string} O tempo formatado.
 */
function formatTimeAgo(timestamp) {
  if (!timestamp) return '';

  const now = new Date();
  const past = new Date(timestamp);
  const seconds = Math.floor((now - past) / 1000);

  let interval = seconds / 31536000;
  if (interval > 1) return `há ${Math.floor(interval)} anos`;

  interval = seconds / 2592000;
  if (interval > 1) return `há ${Math.floor(interval)} meses`;

  interval = seconds / 86400;
  if (interval > 1) return `há ${Math.floor(interval)} dias`;

  interval = seconds / 3600;
  if (interval > 1) return `há ${Math.floor(interval)}h`;

  interval = seconds / 60;
  if (interval > 1) return `há ${Math.floor(interval)}min`;

  return 'agora';
}

/**
 * Traduz o status interno do sistema para um texto amigável em português.
 * @param {string} status - O status interno (ex: 'awaiting_name').
 * @returns {string} O status traduzido.
 */
function translateStatus(status) {
  if (status === 'idle') return 'Ocioso';
  if (!status) return 'Em Aberto';

  const statusMap = {
    'greeted': 'Iniciando Atendimento',
    'awaiting_name': 'Aguardando Nome',
    'awaiting_faq_question': 'Aguardando Dúvida',
    'awaiting_quantity': 'Escolhendo Quantidade',
    'awaiting_street': 'Digitando Endereço (Rua)',
    'awaiting_number': 'Digitando Endereço (Número)',
    'awaiting_neighborhood': 'Digitando Endereço (Bairro)',
    'awaiting_reference': 'Digitando Ponto de Referência',
    'awaiting_delivery_type': 'Escolhendo Entrega/Retirada',
    'viewing_cart': 'Carrinho Aberto',
    'awaiting_customer_action': 'Aguardando Cliente',
    'awaiting_suggestion': 'Aguardando Sugestão',
    'awaiting_payment_method': 'Escolhendo Pagamento',
  };

  // Retorna a tradução ou um texto formatado como fallback
  return statusMap[status] || status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

function CustomerCard({ customer, onSelectCustomer, hasNewMessage }) {
  const isFaqStatus = customer.status === 'awaiting_faq_question';

  const cardClasses = `${styles.card} ${isFaqStatus ? styles.faqAlert : ''}`;
  const statusClasses = `${styles.status} ${isFaqStatus ? styles.statusFaqAlert : ''}`;

  return (
    <div className={cardClasses} onClick={() => onSelectCustomer(customer)}>
      <div className={styles.header}>
        <h4>{customer.name || 'Nome não definido'}</h4>
        {hasNewMessage && <span className={styles.newMessageIndicator}></span>}
        <span className={styles.timeAgo}>{formatTimeAgo(customer.lastInteractionTimestamp)}</span>
      </div>
      <p className={statusClasses}>
        Status: {translateStatus(customer.status)}
      </p>
      {customer.actionLog && customer.actionLog.length > 0 && (
        <p className={styles.lastAction}>
          Última ação: "
          {customer.actionLog[customer.actionLog.length - 1].type === 'text'
            ? customer.actionLog[customer.actionLog.length - 1].text
            : customer.actionLog[customer.actionLog.length - 1].text ||
              customer.actionLog[customer.actionLog.length - 1].id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
          }"
        </p>
      )}
      {(!customer.actionLog || customer.actionLog.length === 0) && (
        <p className={styles.lastAction}>Nenhuma ação recente</p>
      )}
      <p className={styles.customerId}>ID: {customer.id}</p>
    </div>
  );
}

export default CustomerCard;